import type { Group } from "./deriveGroup.js";

/** Command Center sidebar modules (superadmin configures per church). */
export const ADMIN_MODULES = [
  "today",
  "attention",
  "pulse",
  "content",
  "questions",
  "prayer",
  "moderation",
  "people",
  "fams",
  "team",
  "decisions",
  "rewards",
  "notifications",
  "settings",
] as const;
export type AdminModuleKey = (typeof ADMIN_MODULES)[number];

/** Content area tabs inside the Command Center. */
export const CONTENT_TABS = [
  "dailyWords",
  "dailyPrayers",
  "oneThings",
  "paths",
  "shots",
  "seasons",
  "challenges",
  "cohorts",
] as const;
export type ContentTabKey = (typeof CONTENT_TABS)[number];

/** Bottom navigation in the mobile app. */
export const MOBILE_TABS = ["home", "grow", "challenges", "community", "me"] as const;
export type MobileTabKey = (typeof MOBILE_TABS)[number];

/** Rows on the Me tab (settings gear stays available). */
export const MOBILE_ME_ITEMS = [
  "credentials",
  "points",
  "gym",
  "journal",
  "prayFor",
  "fam",
  "myTeam",
  "followJesus",
  "invest",
  "talk",
  "care",
  "myChurch",
] as const;
export type MobileMeItemKey = (typeof MOBILE_ME_ITEMS)[number];

/**
 * Fine-grained app capabilities (design doc “Wer sieht was”). Role defaults come from
 * {@link APP_FEATURE_DEFAULTS}; a church may only turn features off for its members.
 */
export const APP_FEATURES = [
  "guestMode",
  "accountCreate",
  "myChurch",
  "languageQuiet",
  "notifications",
  "dailyWord",
  "celebration",
  "activeQuest",
  "balanceRing",
  "oneThingToday",
  "followUpPrompt",
  "challengesBrowse",
  "challengeCheckIn",
  "cohortChat",
  "challengeShareCard",
  "stayTogetherFam",
  "feed",
  "prayerWallRead",
  "prayerShare",
  "prayerComments",
  "famChat",
  "directMessages",
  "famIdeas",
  "certificates",
  "gym",
  "journal",
  "heyFam",
  "myTeam",
  "prayFor",
  "invest",
  "talkSomeone",
  "faithCheck",
  "careView",
  "decisionsTake",
  "leadFam",
  "commandCenter",
] as const;
export type AppFeatureKey = (typeof APP_FEATURES)[number];

export type ChurchFeaturesStored = {
  admin?: Partial<Record<AdminModuleKey, boolean>>;
  contentTabs?: Partial<Record<ContentTabKey, boolean>>;
  mobile?: {
    tabs?: Partial<Record<MobileTabKey, boolean>>;
    me?: Partial<Record<MobileMeItemKey, boolean>>;
  };
  app?: Partial<Record<AppFeatureKey, boolean>>;
};

const G: Record<Group, number> = { guest: 0, global: 1, hey_church: 2, leader: 3, pastor: 4 };

/** Role defaults from Feature-Matrix.md (1 = on). Order: guest, global, hey_church, leader, pastor. */
const M: Record<AppFeatureKey, readonly [number, number, number, number, number]> = {
  guestMode: [1, 0, 0, 0, 0],
  accountCreate: [1, 1, 1, 1, 1],
  myChurch: [1, 1, 1, 1, 1],
  languageQuiet: [1, 1, 1, 1, 1],
  notifications: [0, 1, 1, 1, 1],
  dailyWord: [1, 1, 1, 1, 1],
  celebration: [1, 1, 1, 1, 1],
  activeQuest: [1, 1, 1, 1, 1],
  balanceRing: [0, 1, 1, 1, 1],
  oneThingToday: [0, 0, 1, 1, 1],
  followUpPrompt: [0, 0, 1, 1, 1],
  challengesBrowse: [1, 1, 1, 1, 1],
  challengeCheckIn: [1, 1, 1, 1, 1],
  cohortChat: [1, 1, 1, 1, 1],
  challengeShareCard: [1, 1, 1, 1, 1],
  stayTogetherFam: [1, 1, 1, 1, 1],
  feed: [0, 1, 1, 1, 1],
  prayerWallRead: [1, 1, 1, 1, 1],
  prayerShare: [1, 1, 1, 1, 1],
  prayerComments: [0, 1, 1, 1, 1],
  famChat: [0, 1, 1, 1, 1],
  directMessages: [0, 1, 1, 1, 1],
  famIdeas: [0, 1, 1, 1, 1],
  certificates: [1, 1, 1, 1, 1],
  gym: [1, 1, 1, 1, 1],
  journal: [0, 1, 1, 1, 1],
  heyFam: [1, 1, 1, 1, 1],
  myTeam: [0, 1, 1, 1, 1],
  prayFor: [0, 1, 1, 1, 1],
  invest: [1, 1, 1, 1, 1],
  talkSomeone: [1, 1, 1, 1, 1],
  faithCheck: [0, 1, 1, 1, 1],
  careView: [0, 0, 0, 0, 1],
  decisionsTake: [0, 0, 0, 0, 1],
  leadFam: [0, 0, 0, 1, 1],
  commandCenter: [0, 0, 0, 1, 1],
};

export const APP_FEATURE_DEFAULTS: Record<AppFeatureKey, Record<Group, boolean>> = Object.fromEntries(
  APP_FEATURES.map((k) => [k, Object.fromEntries((["guest", "global", "hey_church", "leader", "pastor"] as const).map((g) => [g, M[k][G[g]] === 1])) as Record<Group, boolean>]),
) as Record<AppFeatureKey, Record<Group, boolean>>;

export function parseChurchFeatures(settings: unknown): ChurchFeaturesStored {
  if (!settings || typeof settings !== "object") return {};
  const root = settings as { features?: ChurchFeaturesStored };
  return root.features ?? {};
}

function allTrue<K extends string>(keys: readonly K[]): Record<K, boolean> {
  return Object.fromEntries(keys.map((k) => [k, true])) as Record<K, boolean>;
}

export function resolveAdminModules(stored: ChurchFeaturesStored, opts: { superadminGlobal: boolean }): Record<AdminModuleKey, boolean> {
  const base = allTrue(ADMIN_MODULES);
  if (opts.superadminGlobal) return base;
  for (const k of ADMIN_MODULES) {
    if (stored.admin?.[k] === false) base[k] = false;
    if (stored.admin?.[k] === true) base[k] = true;
  }
  return base;
}

export function resolveContentTabs(stored: ChurchFeaturesStored, adminModules: Record<AdminModuleKey, boolean>): Record<ContentTabKey, boolean> {
  const base = allTrue(CONTENT_TABS);
  if (!adminModules.content) {
    for (const k of CONTENT_TABS) base[k] = false;
    return base;
  }
  for (const k of CONTENT_TABS) {
    if (stored.contentTabs?.[k] === false) base[k] = false;
    if (stored.contentTabs?.[k] === true) base[k] = true;
  }
  return base;
}

export function resolveMobileTabs(stored: ChurchFeaturesStored): Record<MobileTabKey, boolean> {
  const base = allTrue(MOBILE_TABS);
  for (const k of MOBILE_TABS) {
    if (stored.mobile?.tabs?.[k] === false) base[k] = false;
    if (stored.mobile?.tabs?.[k] === true) base[k] = true;
  }
  return base;
}

/** Platform setting (church_id NULL) for logged-out guests and accounts with no church and no Fam. */
export const GUEST_MOBILE_TABS_KEY = "guest_mobile_tabs";

export type GuestMobileTabsStored = {
  tabs?: Partial<Record<MobileTabKey, boolean>>;
};

export function parseGuestMobileTabs(value: unknown): GuestMobileTabsStored {
  if (!value || typeof value !== "object") return {};
  const raw = (value as GuestMobileTabsStored).tabs;
  if (!raw || typeof raw !== "object") return {};
  const tabs: Partial<Record<MobileTabKey, boolean>> = {};
  for (const k of MOBILE_TABS) {
    if (raw[k] === true || raw[k] === false) tabs[k] = raw[k];
  }
  return { tabs };
}

export function mergeGuestMobileTabs(prev: GuestMobileTabsStored, patch: GuestMobileTabsStored): GuestMobileTabsStored {
  return { tabs: { ...prev.tabs, ...patch.tabs } };
}

/** Guest bottom navigation. Missing flags stay on, matching a church with no mobile overrides. */
export function resolveGuestMobileTabs(stored: GuestMobileTabsStored): Record<MobileTabKey, boolean> {
  return resolveMobileTabs({ mobile: { tabs: stored.tabs } });
}

export function resolveMobileMe(stored: ChurchFeaturesStored): Record<MobileMeItemKey, boolean> {
  const base = allTrue(MOBILE_ME_ITEMS);
  for (const k of MOBILE_ME_ITEMS) {
    if (stored.mobile?.me?.[k] === false) base[k] = false;
    if (stored.mobile?.me?.[k] === true) base[k] = true;
  }
  return base;
}

/** Church may only disable app features for its members; role matrix still applies. */
export function resolveAppFeatures(stored: ChurchFeaturesStored, group: Group): Record<AppFeatureKey, boolean> {
  const out = {} as Record<AppFeatureKey, boolean>;
  for (const k of APP_FEATURES) {
    out[k] = APP_FEATURE_DEFAULTS[k][group];
    if (stored.app?.[k] === false) out[k] = false;
  }
  return out;
}

export type ResolvedChurchFeatures = {
  admin: Record<AdminModuleKey, boolean>;
  contentTabs: Record<ContentTabKey, boolean>;
  mobile: { tabs: Record<MobileTabKey, boolean>; me: Record<MobileMeItemKey, boolean> };
  app: Record<AppFeatureKey, boolean>;
};

/** Overlay a partial feature-matrix patch onto the stored flags. Missing sections are left as they are. */
export function mergeChurchFeatures(prev: ChurchFeaturesStored, patch: ChurchFeaturesStored): ChurchFeaturesStored {
  return {
    admin: { ...prev.admin, ...patch.admin },
    contentTabs: { ...prev.contentTabs, ...patch.contentTabs },
    mobile: {
      tabs: { ...prev.mobile?.tabs, ...patch.mobile?.tabs },
      me: { ...prev.mobile?.me, ...patch.mobile?.me },
    },
    app: { ...prev.app, ...patch.app },
  };
}

export function resolveChurchFeatures(
  settings: unknown,
  ctx: { group: Group; superadminGlobal: boolean },
): ResolvedChurchFeatures {
  const stored = parseChurchFeatures(settings);
  const admin = resolveAdminModules(stored, { superadminGlobal: ctx.superadminGlobal });
  return {
    admin,
    contentTabs: resolveContentTabs(stored, admin),
    mobile: { tabs: resolveMobileTabs(stored), me: resolveMobileMe(stored) },
    app: resolveAppFeatures(stored, ctx.group),
  };
}

/** Preset for Hey Church Hamburg demo: slim Command Center + Grow paths content + full mobile unless turned off. */
export const HAMBURG_FEATURES_PRESET: ChurchFeaturesStored = {
  admin: {
    today: true,
    pulse: true,
    content: true,
    settings: true,
    attention: false,
    questions: false,
    prayer: false,
    moderation: false,
    people: false,
    fams: false,
    team: false,
    decisions: false,
    rewards: false,
    notifications: false,
  },
  contentTabs: {
    paths: true,
    dailyWords: false,
    dailyPrayers: false,
    oneThings: false,
    shots: false,
    seasons: false,
    challenges: false,
    cohorts: false,
  },
};

export const ADMIN_MODULE_ROUTE: Array<{ prefix: string; module: AdminModuleKey }> = [
  { prefix: "/admin/today", module: "today" },
  { prefix: "/admin/attention", module: "attention" },
  { prefix: "/admin/pulse", module: "pulse" },
  { prefix: "/admin/content", module: "content" },
  { prefix: "/admin/questions", module: "questions" },
  { prefix: "/admin/prayer", module: "prayer" },
  { prefix: "/admin/moderation", module: "moderation" },
  { prefix: "/admin/people", module: "people" },
  { prefix: "/admin/fams", module: "fams" },
  { prefix: "/admin/team", module: "team" },
  { prefix: "/admin/decisions", module: "decisions" },
  { prefix: "/admin/rewards", module: "rewards" },
  { prefix: "/admin/notifications", module: "notifications" },
  { prefix: "/admin/church", module: "settings" },
  { prefix: "/admin/settings", module: "settings" },
];

export const MOBILE_ROUTE_FEATURES: Array<{ prefix: string; tab?: MobileTabKey; feature?: AppFeatureKey }> = [
  { prefix: "/home", tab: "home" },
  { prefix: "/grow", tab: "grow" },
  { prefix: "/challenges", tab: "challenges" },
  { prefix: "/cohorts", tab: "challenges", feature: "challengesBrowse" },
  { prefix: "/feed", tab: "community", feature: "feed" },
  { prefix: "/fams", tab: "community", feature: "famChat" },
  { prefix: "/prayer", tab: "community", feature: "prayerWallRead" },
  { prefix: "/gym", feature: "gym" },
  { prefix: "/team", feature: "myTeam" },
  { prefix: "/decide", feature: "talkSomeone" },
  { prefix: "/care", feature: "careView" },
];
